import { useEffect, useState } from "react";
import { router } from "@inertiajs/react";
import Button from "@/components/atoms/Button";
import useAccess from "@/hooks/useAccess";
import { Pencil, Save, X, AlertCircle } from "lucide-react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-monokai";
import "ace-builds/src-noconflict/theme-github";
import { useThemeStore } from "@/store";
import ace from "ace-builds";

type Props = {
  data: any;
  redirect_props?: any;
};

const baseURL = import.meta.env.BASE_URL || "/";

ace.config.setModuleUrl("ace/mode/json_worker", `${baseURL}workers/worker-json.js`);

const SettingsTab = ({ data, redirect_props }: Props) => {
  const theme = useThemeStore((state) => state.theme);
  const { getAccess } = useAccess();
  const [edit, setEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [jsonData, setJsonData] = useState<string>(JSON.stringify(data || {}, null, 2));
  const [error, setError] = useState<string | null>(null);
  const [editorTheme, setEditorTheme] = useState<string>("github");

  

  useEffect(() => {
    const isDarkMode = theme === "dark";
    setEditorTheme(isDarkMode ? "monokai" : "github");
  }, [theme]);

  const onSubmit = () => {
    try {
      const parsedData = JSON.parse(jsonData);
      
      const payload = {
        global_settings: {
          color_settings: parsedData.color_settings,
          status_colors: parsedData.status_colors,
        },
      };

      router.put(`/global_settings`, payload, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });

      setEdit(false);
      setError(null);
    } catch (error) {
      setError("Invalid JSON format. Please fix errors before saving.");
    }
  };

  useEffect(() => {
    if (redirect_props?.errors) {
      setEdit(false);
    }
  }, [redirect_props]);

  return (
    <div className="p-4">
      {!edit ? (
        <div className="p-4 rounded-md">
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => setEdit(true)}
              disabled={!getAccess(["update_global_settings"])}
            >
              <Pencil size={16} className="mr-2" />
              Edit
            </Button>
          </div>
          <pre className="overflow-auto whitespace-pre-wrap text-sm">{jsonData}</pre>
          
        </div>
      ) : (
        <div className="p-4 rounded-md">
          <div className="flex justify-end space-x-2 mt-4">
            <Button onClick={() => setEdit(false)} variant="secondary">
              <X size={16} className="mr-2" />
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={loading}>
              <Save size={16} className="mr-2" />
              Save
            </Button>
          </div>

          <AceEditor
            mode="json"
            theme={editorTheme}
            value={jsonData}
            onChange={(newValue) => setJsonData(newValue)}
            name="json-editor"
            editorProps={{ $blockScrolling: true }}
            className="w-full h-64"
            showPrintMargin={false}
            setOptions={{
              useWorker: true,
              tabSize: 2,
            }}
          />

          {error && (
            <div className="flex items-center bg-red-500 text-white p-2 mt-2 rounded-md">
              <AlertCircle size={16} className="mr-2" />
              <span>{error}</span>
            </div>
          )}

          
        </div>
      )}
    </div>
  );
};

export default SettingsTab;
